import RefreshIcon from '@mui/icons-material/Refresh';
import Alert from '@mui/material/Alert';
import { Box, Button, Chip, CircularProgress, Stack, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useEffect, useRef, useState } from 'react';
import { SectionCard } from '../../../components/Section';
import { TREASURY_ONBOARDING_POLL_MS } from '../../../constants/treasury';
import { useFetchTreasuryOnboardingQuery } from '../../../redux/features/treasury/treasury.apiSlice';
import { OnboardingEmployeeRaw } from '../../../types/b2tRatibiPlus';
import { TreasuryCompanySetup } from '../../../types/treasury';
import { getTreasuryErrorMessage } from '../../../utils/treasury';
import { CreateCompanyEmployee } from '../Employees/CreateCompanyEmployee';

type TreasuryEmployeeStepProps = {
  setup: TreasuryCompanySetup;
  onDone: () => void;
};

/**
 * Step 2 — the treasury employee (the company owner). The create call only QUEUES the onboarding, so this reads the
 * treasury onboarding row: queued / processing → wait (re-read every few seconds); failed → the error and the form
 * again; completed → the setup now has its employee and the page moves on.
 */
export const TreasuryEmployeeStep = ({ setup, onDone }: TreasuryEmployeeStepProps) => {
  const { enqueueSnackbar } = useSnackbar();
  // Re-read every few seconds while the onboarding runs (polling stops once it isn't).
  const [isPolling, setIsPolling] = useState(false);
  const {
    data,
    isLoading,
    isError,
    error,
    refetch: loadOnboarding,
  } = useFetchTreasuryOnboardingQuery(setup.companyId, {
    pollingInterval: isPolling ? TREASURY_ONBOARDING_POLL_MS : 0,
  });
  const latest: OnboardingEmployeeRaw | null = data?.data?.[0] ?? null;

  useEffect(() => {
    if (isError) {
      enqueueSnackbar(getTreasuryErrorMessage(error, 'Failed to load the treasury onboarding'), { variant: 'error' });
    }
  }, [isError, error, enqueueSnackbar]);

  const isRunning = latest?.status === 'queued' || latest?.status === 'processing';
  const isCompleted = latest?.status === 'completed';

  useEffect(() => {
    setIsPolling(isRunning);
  }, [isRunning]);

  // Reload the page only when the onboarding FINISHES while this step is open. onDone remounts this step, so calling
  // it whenever the status reads `completed` looped forever for a setup that still has no employee. A step that
  // opens on an already-completed onboarding shows a message with a manual Refresh instead (below).
  const wasRunning = useRef(false);

  useEffect(() => {
    if (isRunning) {
      wasRunning.current = true;
    } else if (isCompleted && wasRunning.current) {
      wasRunning.current = false;
      onDone();
    }
  }, [isRunning, isCompleted, onDone]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isRunning) {
    return (
      <SectionCard
        title="Onboarding in progress"
        description="This page updates by itself when it is done."
        action={
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip size="small" color="info" label={latest?.status} />
            <Button size="small" startIcon={<RefreshIcon />} onClick={loadOnboarding}>
              Refresh
            </Button>
          </Stack>
        }
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <CircularProgress size={20} />
          <Typography variant="body2">Onboarding {latest?.email}…</Typography>
        </Stack>
      </SectionCard>
    );
  }

  // Finished, but the setup this page loaded has no employee yet (or it finished between two reads). A press, not an
  // automatic reload — reloading by itself here is what looped.
  if (isCompleted) {
    return (
      <Alert
        severity="info"
        action={
          <Button color="inherit" size="small" startIcon={<RefreshIcon />} onClick={onDone}>
            Refresh
          </Button>
        }
      >
        Onboarding {latest?.email} is done. Refresh to continue.
      </Alert>
    );
  }

  return (
    <Stack spacing={2}>
      {latest?.status === 'failed' && (
        <Alert severity="error">
          The last onboarding failed{latest.error_message ? `: ${latest.error_message}` : ''}. Fix the details and
          submit again.
        </Alert>
      )}
      <SectionCard
        title="Owner Details"
        description={`Joins the "${setup.group.name}" group and "${setup.department.name}" department automatically.`}
      >
        <CreateCompanyEmployee
          treasury={{
            companyId: setup.companyId,
            groupName: setup.group.name,
            departmentName: setup.department.name,
            onDone: loadOnboarding,
          }}
        />
      </SectionCard>
    </Stack>
  );
};
